import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Trash2, ArrowLeft, GitBranch } from 'lucide-react';
import { AppDispatch, RootState } from '../../store/store';
import {
  fetchDistributionRules,
  createRule,
  updateRule,
  deleteRule,
  selectDistributionRules,
  selectDistributionLoading,
} from '../../store/slices/distributionSlice';
import { DistributionRule } from '../../services/distributionService';
import './leadDistributionSettings.scss';

// Constants
const SOURCES = [
  { value: '64109561e6c7e737227a6382', label: 'Lead Form' },
  { value: '64109561e6c7e737227a6384', label: 'Facebook' },
  { value: '64109561e6c7e737227a6383', label: 'IndiaMART' },
  { value: '64109561e6c7e737227a6385', label: 'TradeIndia' },
  { value: '64109561e6c7e737227a637c', label: 'CSV' },
] as const;

const OPERATORS = [
  { value: 'eq', label: 'Equal' },
  { value: 'ne', label: 'Not Equal' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'lt', label: 'Less Than' },
  { value: 'contain', label: 'Contains' },
  { value: 'doesNotContain', label: 'Does Not Contain' },
];

const LeadDistributionSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const rules = useSelector(selectDistributionRules);
  const loading = useSelector(selectDistributionLoading);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingRule, setEditingRule] = useState<DistributionRule | null>(null);

  useEffect(() => {
    dispatch(fetchDistributionRules());
  }, [dispatch]);

  const handleToggleStatus = async (rule: DistributionRule) => {
    await dispatch(updateRule({ ...rule, status: !rule.status }));
    dispatch(fetchDistributionRules());
  };

  const handleDelete = async (ruleId: string) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      await dispatch(deleteRule(ruleId));
      dispatch(fetchDistributionRules());
    }
  };

  const handleEdit = (rule: DistributionRule) => {
    setEditingRule(rule);
    setShowForm(true);
  };

  const handleSave = async (rule: DistributionRule) => {
    if (editingRule) {
      await dispatch(updateRule({ ...rule, _id: editingRule._id }));
    } else {
      await dispatch(createRule(rule));
    }
    dispatch(fetchDistributionRules());
    setShowForm(false);
    setEditingRule(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRule(null);
  };

  if (showForm) {
    return <RuleForm onCancel={handleCancel} onSave={handleSave} editRule={editingRule} />;
  }

  return (
    <div className="lead-distribution-settings">
      <div className="distribution-header">
        <div>
          <h2>Distribution Rules</h2>
          <p>Manage how leads are automatically distributed to team members</p>
        </div>
        <button className="btn-create-rule" onClick={() => setShowForm(true)}>
          <Plus size={16} />
          Create Rule
        </button>
      </div>

      <div className="rules-list">
        {loading.rules ? (
          <div className="loading-state">Loading rules...</div>
        ) : rules.length === 0 ? (
          <div className="empty-state">
            <GitBranch size={64} />
            <h3>No distribution rules</h3>
            <p>Create rules to automatically distribute leads based on source and criteria</p>
            <button className="btn-primary" onClick={() => setShowForm(true)}>
              <Plus size={16} />
              Create Your First Rule
            </button>
          </div>
        ) : (
          rules.map((rule: DistributionRule) => (
            <div key={rule._id} className="rule-card" onClick={() => handleEdit(rule)}>
              <div className="rule-card-content">
                <div className="rule-info">
                  <h4>{rule.name}</h4>
                  <p className="rule-source">
                    {SOURCES.find((s) => s.value === rule.integration)?.label || 'Unknown Source'}
                  </p>
                  {rule.ruleConfig?.fieldName && (
                    <p className="rule-condition">
                      {rule.ruleConfig.fieldName}: {rule.ruleConfig.fieldValue}
                    </p>
                  )}
                  <p className="rule-stats">
                    Distributed: {rule.totalDistributed || 0} | Runs: {rule.totalNoOfRun || 0}
                  </p>
                  <p className="rule-creator">Created by: {rule.createdBy || 'Unknown'}</p>
                </div>
                <div className="rule-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="btn-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(rule._id!);
                    }}
                    title="Delete rule"
                  >
                    <Trash2 size={16} />
                  </button>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={rule.status || false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleToggleStatus(rule);
                      }}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

interface RuleFormProps {
  onCancel: () => void;
  onSave: (rule: DistributionRule) => void;
  editRule: DistributionRule | null;
}

const RuleForm: React.FC<RuleFormProps> = ({ onCancel, onSave, editRule }) => {
  const dispatch = useDispatch<AppDispatch>();
  const teamMembers = useSelector((state: RootState) => state.whatsapp.team.members);

  const [formData, setFormData] = useState({
    name: editRule?.name || '',
    source: editRule?.integration || '',
    fieldName: editRule?.ruleConfig?.fieldName || '',
    operator: editRule?.ruleConfig?.custom?.[1]?.operator || 'eq',
    values: editRule?.ruleConfig?.fieldValue?.split(',') || [],
    currentValue: '',
  });

  const [selectedMembers, setSelectedMembers] = useState<{ [key: string]: boolean }>(
    editRule?.recipientsIds?.ids
      ? Object.keys(editRule.recipientsIds.ids).reduce((acc, id) => {
          acc[id] = editRule.recipientsIds!.ids[id].status;
          return acc;
        }, {} as { [key: string]: boolean })
      : {}
  );

  const [error, setError] = useState<string>('');

  useEffect(() => {
    // Fetch team members if not loaded
    if (!teamMembers || teamMembers.length === 0) {
      dispatch({ type: 'team/fetchMembers' });
    }
  }, [dispatch, teamMembers]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Rule name is required');
      return;
    }
    if (!formData.source) {
      setError('Please select a lead source');
      return;
    }
    if (!formData.fieldName.trim()) {
      setError('Field name is required');
      return;
    }
    if (formData.values.length === 0) {
      setError('Please add at least one value');
      return;
    }
    if (Object.keys(selectedMembers).filter(id => selectedMembers[id]).length === 0) {
      setError('Please select at least one team member');
      return;
    }

    const recipientsIds: any = {
      ids: {},
      cursor: editRule?.recipientsIds?.cursor || '',
      current_distribution: editRule?.recipientsIds?.current_distribution || '',
    };

    Object.keys(selectedMembers).forEach((id) => {
      if (selectedMembers[id]) {
        recipientsIds.ids[id] = { waitage: 1, status: true };
      }
    });

    const rule: DistributionRule = {
      name: formData.name,
      integration: formData.source,
      ruleConfig: {
        custom: [
          {
            integration: formData.source,
            users: Object.keys(selectedMembers).filter(id => selectedMembers[id]),
          },
          {
            key: formData.fieldName,
            value: formData.values.join(','),
            operator: formData.operator,
            users: Object.keys(selectedMembers).filter(id => selectedMembers[id]),
          },
        ],
      },
      recipientsIds,
      status: true,
    };

    onSave(rule);
  };

  const addValue = () => {
    if (formData.currentValue.trim()) {
      setFormData((prev) => ({
        ...prev,
        values: [...prev.values, prev.currentValue.trim()],
        currentValue: '',
      }));
    }
  };

  const removeValue = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      values: prev.values.filter((_, i) => i !== index),
    }));
  };

  const toggleMember = (memberId: string) => {
    setSelectedMembers((prev) => ({
      ...prev,
      [memberId]: !prev[memberId],
    }));
  };

  return (
    <div className="rule-form">
      <div className="form-header">
        <button className="btn-back" onClick={onCancel}>
          <ArrowLeft size={20} />
          Back
        </button>
        <h2>{editRule ? 'Edit Distribution Rule' : 'Create Distribution Rule'}</h2>
      </div>

      {error && <div className="alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-card">
          <div className="form-group">
            <label>Rule Name*</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Enter rule name"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Lead Source*</label>
            <select
              value={formData.source}
              onChange={(e) => setFormData((prev) => ({ ...prev, source: e.target.value }))}
              className="form-select"
            >
              <option value="">Select source</option>
              {SOURCES.map((source) => (
                <option key={source.value} value={source.value}>
                  {source.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Field Name*</label>
            <input
              type="text"
              value={formData.fieldName}
              onChange={(e) => setFormData((prev) => ({ ...prev, fieldName: e.target.value }))}
              placeholder="Enter field name"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Operator*</label>
            <select
              value={formData.operator}
              onChange={(e) => setFormData((prev) => ({ ...prev, operator: e.target.value }))}
              className="form-select"
            >
              {OPERATORS.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Values*</label>
            <div className="value-input-group">
              <input
                type="text"
                value={formData.currentValue}
                onChange={(e) => setFormData((prev) => ({ ...prev, currentValue: e.target.value }))}
                placeholder="Type and press Add"
                className="form-input"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addValue();
                  }
                }}
              />
              <button type="button" className="btn-add" onClick={addValue}>
                Add
              </button>
            </div>
            <div className="values-list">
              {formData.values.map((value, index) => (
                <span key={index} className="value-badge">
                  {value}
                  <button type="button" onClick={() => removeValue(index)} className="badge-remove">
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Distribute to Team Members*</label>
            <div className="team-members-list">
              {teamMembers && teamMembers.length > 0 ? (
                teamMembers
                  .filter((member: any) => member.isActive)
                  .map((member: any) => (
                    <div key={member._id} className="team-member-item">
                      <label className="team-member-label">
                        <input
                          type="checkbox"
                          checked={selectedMembers[member._id] || false}
                          onChange={() => toggleMember(member._id)}
                        />
                        <span>
                          {member.firstName} {member.lastName}
                        </span>
                      </label>
                    </div>
                  ))
              ) : (
                <p className="no-members">No team members available</p>
              )}
            </div>
          </div>

          <div className="rule-preview">
            <h6>Rule Preview:</h6>
            <p>
              When lead source is{' '}
              <strong>{SOURCES.find((s) => s.value === formData.source)?.label || '...'}</strong>{' '}
              and <strong>{formData.fieldName || '...'}</strong>{' '}
              <strong>{OPERATORS.find((o) => o.value === formData.operator)?.label || '...'}</strong>
              :{' '}
              <strong className="preview-values">{formData.values.join(', ') || '...'}</strong>
            </p>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-save">
            {editRule ? 'Update Rule' : 'Save Rule'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadDistributionSettings;